import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from 'react-i18next';
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Dialog from "../../components/ui/Dialog";
import Input from "../../components/ui/Input";
import { marketplaceService, Company } from "../../services/marketplace.service";
import { Permission } from "../../permissions/permission.enum";
import { useAuth } from "../../context/AuthContext";
import { getErrorMsg } from "../../utils/error";
import { useConfirm } from '../../hooks/useConfirm';

type StatusTab = "pending" | "active" | "rejected" | "deleted";

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

// ─── Company Detail Drawer ────────────────────────────────────────────────────

interface CompanyDrawerProps {
  companyId: string | null;
  onClose: () => void;
  onApprove: (company: Company) => Promise<boolean>;
  onOpenReject: (company: Company) => void;
}

const CompanyDrawer = ({ companyId, onClose, onApprove, onOpenReject }: CompanyDrawerProps) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [approving, setApproving] = useState(false);
  const [showBrDownloadInput, setShowBrDownloadInput] = useState(false);
  const [brFilePasswordInput, setBrFilePasswordInput] = useState("");
  const [brFileDownloadError, setBrFileDownloadError] = useState<string | null>(null);
  const [downloadingBr, setDownloadingBr] = useState(false);
  const [showBrMasterKeyInput, setShowBrMasterKeyInput] = useState(false);
  const [brMasterKeyInput, setBrMasterKeyInput] = useState("");
  const [brRevealError, setBrRevealError] = useState<string | null>(null);
  const [revealingBrPassword, setRevealingBrPassword] = useState(false);
  const [brDocPassword, setBrDocPassword] = useState<string | null>(null);

  const handleDownloadBr = async () => {
    if (!companyId) return;
    setBrFileDownloadError(null);
    setDownloadingBr(true);
    try {
      const blob = await marketplaceService.downloadCompanyBrDocument(companyId, brFilePasswordInput);
      const ext = detail?.brFileUrl?.split(".").pop()?.split("?")[0] || "bin";
      const filename = detail?.brFileUrl?.split("/").pop()?.split("?")[0] ?? `br-document.${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setBrFileDownloadError(t('file_password_decrypt_error'));
    } finally {
      setDownloadingBr(false);
    }
  };

  const handleRevealBrPassword = async () => {
    if (!companyId) return;
    setBrRevealError(null);
    setRevealingBrPassword(true);
    try {
      const result = await marketplaceService.getCompanyDocumentPassword(companyId, brMasterKeyInput);
      setBrDocPassword(result?.password ?? null);
    } catch {
      setBrRevealError(t('decrypt_error'));
    } finally {
      setRevealingBrPassword(false);
    }
  };

  useEffect(() => {
    if (!companyId) {
      setDetail(null);
      setShowBrDownloadInput(false);
      setBrFilePasswordInput("");
      setBrFileDownloadError(null);
      setShowBrMasterKeyInput(false);
      setBrMasterKeyInput("");
      setBrRevealError(null);
      setBrDocPassword(null);
      return;
    }
    setLoading(true);
    setError("");
    setShowBrDownloadInput(false);
    setBrFilePasswordInput("");
    setBrFileDownloadError(null);
    setShowBrMasterKeyInput(false);
    setBrMasterKeyInput("");
    setBrRevealError(null);
    setBrDocPassword(null);
    marketplaceService.getCompany(companyId)
      .then(setDetail)
      .catch((err) => setError(getErrorMsg(err)))
      .finally(() => setLoading(false));
  }, [companyId]);

  const handleApprove = async () => {
    if (!detail) return;
    setApproving(true);
    try {
      const approved = await onApprove(detail);
      if (approved) setDetail((prev) => prev ? { ...prev, status: "active", isVerified: true } : prev);
    } finally {
      setApproving(false);
    }
  };

  if (!companyId) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white dark:bg-gray-900 shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-4">
            {loading ? (
              <div className="h-5 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            ) : (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                {detail?.name ?? "Company"}
              </h2>
            )}
            {detail && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_BADGE[detail.status] ?? "bg-gray-100 text-gray-800"}`}>
                  {detail.status}
                </span>
                {detail.isVerified && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200">
                    Verified
                  </span>
                )}
                <span className="text-xs text-gray-400 font-mono">{detail.slug}</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Pending action bar */}
        {detail?.status === "pending" && (
          <div className="px-5 py-3 bg-yellow-50 dark:bg-yellow-950/30 border-b border-yellow-100 dark:border-yellow-900/40 flex items-center gap-3 flex-shrink-0">
            <span className="text-xs text-yellow-700 dark:text-yellow-400 flex-1">
              This company is awaiting review.
            </span>
            <Button size="sm" variant="primary" onClick={handleApprove} disabled={approving}>
              {approving ? "Approving…" : "Approve"}
            </Button>
            <Button size="sm" variant="danger" onClick={() => detail && onOpenReject(detail)}>
              Reject
            </Button>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading && (
            <div className="p-6 space-y-4">
              {[80, 60, 90, 50, 70].map((w, i) => (
                <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: `${w}%` }} />
              ))}
            </div>
          )}

          {error && (
            <div className="p-6">
              <div className="p-3 rounded bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-900/40">
                {error}
              </div>
            </div>
          )}

          {!loading && !error && detail && (
            <div className="divide-y divide-gray-100 dark:divide-gray-800 pb-8">

              {/* Logo */}
              {detail.logo && (
                <div className="px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">Logo</p>
                  <img
                    src={detail.logo}
                    alt={detail.name}
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200 dark:border-gray-700"
                  />
                </div>
              )}

              {/* Core info */}
              <div className="px-5 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">Details</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  {detail.tradingName && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">Trading Name</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">{detail.tradingName}</p>
                    </div>
                  )}
                  {detail.brId && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">BR Number</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5 font-mono">{detail.brId}</p>
                    </div>
                  )}
                  {detail.primaryContactName && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">Primary Contact</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">{detail.primaryContactName}</p>
                    </div>
                  )}
                  {detail.phone && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">Phone</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">{detail.phone}</p>
                    </div>
                  )}
                  {detail.email && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">Email</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">{detail.email}</p>
                    </div>
                  )}
                  {detail.website && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">Website</span>
                      <a href={detail.website} target="_blank" rel="noreferrer" className="font-medium text-blue-600 dark:text-blue-400 hover:underline mt-0.5 block truncate">
                        {detail.website}
                      </a>
                    </div>
                  )}
                  {detail.location && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">Location</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">{detail.location}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">Submitted</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">{new Date(detail.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500 dark:text-gray-400 text-xs">Company ID</span>
                    <p className="font-mono text-xs text-gray-600 dark:text-gray-400 mt-0.5 break-all">{detail.id}</p>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {detail.bio && (
                <div className="px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Bio</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{detail.bio}</p>
                </div>
              )}

              {/* BR Certificate */}
              <div className="px-5 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">{t('business_registration_document')}</p>
                {detail.brFileUrl || detail.brDocumentUrl ? (
                  <div className="flex flex-col gap-3">
                    <span className="text-sm font-mono text-gray-700 dark:text-gray-300 break-all">
                      {(detail.brFileUrl ?? detail.brDocumentUrl)!.split("/").pop()?.split("?")[0] ?? "br-document"}
                    </span>

                    {/* Download using file password */}
                    <div className="flex flex-col gap-1">
                      {!showBrDownloadInput ? (
                        <Button size="sm" variant="secondary" onClick={() => setShowBrDownloadInput(true)}>
                          {t('download_id_document')}
                        </Button>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <input
                              type="password"
                              value={brFilePasswordInput}
                              onChange={(e) => setBrFilePasswordInput(e.target.value)}
                              placeholder={t('enter_file_password')}
                              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-52"
                            />
                            <Button size="sm" variant="secondary" onClick={handleDownloadBr} disabled={downloadingBr || !brFilePasswordInput}>
                              {downloadingBr ? t('loading') : t('download_id_document')}
                            </Button>
                          </div>
                          {brFileDownloadError && (
                            <span className="text-xs text-red-600 dark:text-red-400">{brFileDownloadError}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Recover file password using master key */}
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        className="text-xs text-blue-600 dark:text-blue-400 underline text-left w-fit"
                        onClick={() => setShowBrMasterKeyInput((v) => !v)}
                      >
                        {t('recover_file_password')}
                      </button>
                      {showBrMasterKeyInput && (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <input
                              type="password"
                              value={brMasterKeyInput}
                              onChange={(e) => setBrMasterKeyInput(e.target.value)}
                              placeholder={t('enter_master_key')}
                              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-52"
                            />
                            <Button size="sm" variant="secondary" onClick={handleRevealBrPassword} disabled={revealingBrPassword || !brMasterKeyInput}>
                              {revealingBrPassword ? t('loading') : t('reveal_doc_password')}
                            </Button>
                          </div>
                          {brRevealError && (
                            <span className="text-xs text-red-600 dark:text-red-400">{brRevealError}</span>
                          )}
                          {brDocPassword && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500 dark:text-gray-400">{t('pdf_open_password')}:</span>
                              <span className="text-xs font-mono bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-300 px-2 py-0.5 rounded select-all">
                                {brDocPassword}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No certificate uploaded</p>
                )}
              </div>

              {/* Admin user */}
              <div className="px-5 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">Company Admin</p>
                {detail.adminUser ? (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">
                        {((detail.adminUser.fullName ?? detail.adminUser.email ?? "U")[0]).toUpperCase()}
                      </span>
                    </div>
                    <div className="text-sm space-y-0.5">
                      {detail.adminUser.fullName && (
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {detail.adminUser.fullName}
                        </p>
                      )}
                      <p className="text-gray-500 dark:text-gray-400">{detail.adminUser.email}</p>
                      {detail.adminUser.registrationStatus && (
                        <p className="text-xs text-gray-400 capitalize">{detail.adminUser.registrationStatus}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 font-mono">{detail.adminId}</p>
                )}
              </div>

            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

const CompaniesPage = () => {
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission(Permission.USERS_UPDATE);
  const { t } = useTranslation('common');
  const { confirm, ConfirmDialog } = useConfirm();

  const [activeTab, setActiveTab] = useState<StatusTab>("pending");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<Company | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  // ── Restore dialog ──────────────────────────────────────────────────────
  const [restoreCompanyId, setRestoreCompanyId] = useState<string | null>(null);
  const [suspendedBrokers, setSuspendedBrokers] = useState<Array<{ id: string; fullName: string; email: string; username: string | null; avatar: string | null }>>([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const loadCompanies = useCallback(async (status: StatusTab, pg: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await marketplaceService.getCompanies({ status, page: pg, take: 20 });
      setCompanies(result.data);
      setTotalPages(result.meta.pageCount);
      setTotalCount(result.meta.itemCount);
    } catch (err) {
      setError(getErrorMsg(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadCompanies(activeTab, 1);
  }, [activeTab, loadCompanies]);

  const handleTabChange = (tab: StatusTab) => {
    setActiveTab(tab);
  };

  const handleOpenRestore = async (companyId: string) => {
    setRestoreCompanyId(companyId);
    setSelectedOwnerId('');
    setRestoreError(null);
    setSuspendedBrokers([]);
    try {
      const brokers = await marketplaceService.getSuspendedBrokers(companyId);
      setSuspendedBrokers(brokers);
    } catch {
      setSuspendedBrokers([]);
      setRestoreError('Could not load suspended brokers — enter a User ID manually.');
    }
  };

  const handleConfirmRestore = async () => {
    if (!restoreCompanyId || !selectedOwnerId) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      await marketplaceService.restoreCompany(restoreCompanyId, selectedOwnerId);
      setRestoreCompanyId(null);
      loadCompanies(activeTab, page);
    } catch (err) {
      setRestoreError(getErrorMsg(err));
    } finally {
      setRestoring(false);
    }
  };

  const handleApprove = async (company: Company): Promise<boolean> => {
    if (!await confirm(t('confirm_approve_company'))) return false;
    try {
      await marketplaceService.approveCompany(company.id);
      loadCompanies(activeTab, page);
      return true;
    } catch (err) {
      setError(getErrorMsg(err, 'Failed to approve'));
      return false;
    }
  };

  const openRejectDialog = (company: Company) => {
    setRejectTarget(company);
    setRejectReason("");
    setRejectError(null);
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) {
      setRejectError("Reason is required.");
      return;
    }
    setActionLoading(true);
    try {
      await marketplaceService.rejectCompany(rejectTarget.id, rejectReason.trim());
      setRejectDialogOpen(false);
      setRejectTarget(null);
      loadCompanies(activeTab, page);
    } catch (err) {
      setRejectError(getErrorMsg(err));
    } finally {
      setActionLoading(false);
    }
  };

  const tabs: { key: StatusTab; label: string }[] = [
    { key: "pending", label: "Pending" },
    { key: "active", label: "Active" },
    { key: "rejected", label: "Rejected" },
    { key: "deleted", label: "Deleted" },
  ];

  return (
    <Layout title="Companies">
      <div className="space-y-6 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-semibold tracking-tight">Companies</h1>
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
                  }`}
                >
                  {tab.label}
                  {tab.key === activeTab && totalCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                      {totalCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </Card>

        <Card className="flex-1 min-h-0 overflow-auto">
          {loading && (
            <div className="p-6 text-gray-500 dark:text-gray-400">Loading…</div>
          )}
          {error && (
            <div className="p-6 text-red-500">{error}</div>
          )}
          {!loading && !error && companies.length === 0 && (
            <div className="p-6 text-gray-500 dark:text-gray-400">
              No {activeTab} companies.
            </div>
          )}
          {!loading && companies.length > 0 && (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Admin Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    BR Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {companies.map((c) => (
                  <tr key={c.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {c.name}
                      </div>
                      {c.tradingName && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Trading: {c.tradingName}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        {c.slug}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {c.primaryContactName && (
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {c.primaryContactName}
                        </div>
                      )}
                      {c.phone && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {c.phone}
                        </div>
                      )}
                      {c.email && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {c.email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {c.adminUser ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {c.adminUser.fullName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {c.adminUser.email}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {c.brFileUrl ?? c.brDocumentUrl ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Uploaded
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_BADGE[c.status] ?? "bg-gray-100 text-gray-800"}`}
                      >
                        {c.status}
                      </span>
                      {c.isVerified && (
                        <span className="ml-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200">
                          Verified
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(c.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setDetailId(c.id)}
                      >
                        View
                      </Button>
                      {canUpdate && activeTab === "pending" && (
                        <>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleApprove(c)}
                            disabled={actionLoading}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => openRejectDialog(c)}
                            disabled={actionLoading}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {canUpdate && activeTab === "deleted" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleOpenRestore(c.id)}
                        >
                          Restore
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && totalPages > 1 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => { setPage((p) => p - 1); loadCompanies(activeTab, page - 1); }}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => { setPage((p) => p + 1); loadCompanies(activeTab, page + 1); }}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <CompanyDrawer
        companyId={detailId}
        onClose={() => setDetailId(null)}
        onApprove={async (company) => {
          const ok = await handleApprove(company);
          if (ok) setDetailId(null);
          return ok;
        }}
        onOpenReject={(company) => {
          setDetailId(null);
          openRejectDialog(company);
        }}
      />

      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        title={`Reject Company: ${rejectTarget?.name ?? ""}`}
      >
        <div className="space-y-4">
          {rejectError && <div className="text-red-500 text-sm">{rejectError}</div>}
          <Input
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Provide a reason for rejection"
          />
          <div className="flex justify-end space-x-2">
            <Button variant="secondary" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRejectSubmit} disabled={actionLoading}>
              Reject
            </Button>
          </div>
        </div>
      </Dialog>
      {ConfirmDialog}

      {restoreCompanyId && (
        <Dialog open onClose={() => setRestoreCompanyId(null)} title="Restore Company">
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select a new company owner. Suspended brokers from this company are listed below.
            </p>
            {suspendedBrokers.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase">Suspended Brokers</p>
                {suspendedBrokers.map((b) => (
                  <label key={b.id} className="flex items-center gap-3 p-2 border rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                    <input
                      type="radio"
                      name="newOwner"
                      value={b.id}
                      checked={selectedOwnerId === b.id}
                      onChange={() => setSelectedOwnerId(b.id)}
                    />
                    <span className="text-sm">{b.fullName} <span className="text-gray-400">({b.email})</span></span>
                  </label>
                ))}
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Or enter a User ID</p>
              <Input
                placeholder="User UUID"
                value={selectedOwnerId}
                onChange={(e) => setSelectedOwnerId(e.target.value)}
              />
            </div>
            {restoreError && <p className="text-sm text-red-500">{restoreError}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setRestoreCompanyId(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!selectedOwnerId || restoring}
                onClick={handleConfirmRestore}
              >
                {restoring ? 'Restoring…' : 'Confirm Restore'}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </Layout>
  );
};

export default CompaniesPage;
